import type { Contribution, CFQuery, CFSummary, CFPageMode, CFMonthlyData, CFTopEntry } from '../types/campaignFinance';

/**
 * Request timeout for campaign-finance queries. These are analytical scans/aggregates
 * over ~2.15M contribution rows (a contributor filter matches a computed name, the
 * type dropdowns aggregate the whole table), which legitimately exceed the app's 10s
 * default — especially under DB load. Matches the heavier Legislation/Voter pages.
 */
export const CF_REQUEST_TIMEOUT_MS = 60000;

export function fmtMoney(n: number, opts?: { compact?: boolean }): string {
  if (opts?.compact) {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtRelative(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

export function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function getPageMode(query: CFQuery): CFPageMode {
  const hasFilter = Object.entries(query).some(([k, v]) => k !== 'scope' && k !== 'mode' && v !== undefined && v !== '');
  if (!hasFilter) return 'empty';
  if (query.contributor) return 'contributor';
  if (query.committee_name || query.candidate) return 'recipient';
  // Payee-centric search with no committee in context lands on the vendor view.
  // Payee + committee stays 'recipient' — that question is "what did X buy from Y",
  // answered by the committee's ledger filtered to the payee.
  if (query.payee || query.vendor_id) return 'vendor';
  return 'general';
}

export function summarizeCF(rows: Contribution[]): CFSummary {
  if (rows.length === 0) {
    return { total: 0, count: 0, contributors: 0, recipients: 0, avg: 0, median: 0, largest: 0 };
  }
  const amounts = rows.map(r => Number(r.contribution_amount) || 0);
  const total = amounts.reduce((s, a) => s + a, 0);
  const contributors = new Set(rows.map(r => r.computed_contributor_name || '').filter(Boolean)).size;
  const recipients = new Set(rows.map(r => r.committee_name || '').filter(Boolean)).size;
  const sorted = [...amounts].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  return {
    total,
    count: rows.length,
    contributors,
    recipients,
    avg: total / rows.length,
    median,
    largest: sorted[sorted.length - 1],
  };
}

export function groupByMonth(rows: Contribution[], opts?: { byParty?: boolean }): CFMonthlyData[] {
  const map = new Map<string, { total: number; count: number }>();
  for (const r of rows) {
    if (!r.contribution_date) continue;
    const monthKey = r.contribution_date.slice(0, 7);
    const partyKey = opts?.byParty ? `${monthKey}|${r.candidate_party || 'Other'}` : monthKey;
    const entry = map.get(partyKey) || { total: 0, count: 0 };
    entry.total += Number(r.contribution_amount) || 0;
    entry.count += 1;
    map.set(partyKey, entry);
  }
  const result: CFMonthlyData[] = [];
  for (const [key, val] of map) {
    if (opts?.byParty) {
      const [month, party] = key.split('|');
      result.push({ month, party, ...val });
    } else {
      result.push({ month: key, ...val });
    }
  }
  return result.sort((a, b) => a.month.localeCompare(b.month));
}

export function topByField(rows: Contribution[], field: 'computed_contributor_name' | 'committee_name' | 'candidate_name', n: number = 10): CFTopEntry[] {
  const map = new Map<string, { total: number; count: number; party?: string }>();
  for (const r of rows) {
    const name = (r[field] as string) || '';
    if (!name) continue;
    const entry = map.get(name) || { total: 0, count: 0 };
    entry.total += Number(r.contribution_amount) || 0;
    entry.count += 1;
    if (r.candidate_party) entry.party = r.candidate_party;
    map.set(name, entry);
  }
  return Array.from(map.entries())
    .map(([name, val]) => ({ name, ...val }))
    .sort((a, b) => b.total - a.total)
    .slice(0, n);
}

export function getContributorDisplayName(c: Contribution): string {
  const name = c.computed_contributor_name
    || `${c.contributor_first_name || ''} ${c.contributor_last_name || ''}`.trim();
  if (name && name.toLowerCase() !== 'unknown') return name;
  if (c.contribution_type?.toLowerCase().includes('non-itemized')) return 'Non-Itemized';
  if (!name || name.toLowerCase() === 'unknown') return 'Anonymous';
  return name;
}

export function getPartyColor(party: string | null | undefined): string {
  if (!party) return 'text-accent-500';
  const p = party.toLowerCase();
  if (p.includes('democrat')) return 'text-party-democrat';
  if (p.includes('republican')) return 'text-party-republican';
  return 'text-accent-500';
}

export function getPartyBgColor(party: string | null | undefined): string {
  if (!party) return 'bg-accent-300';
  const p = party.toLowerCase();
  if (p.includes('democrat')) return 'bg-party-democrat';
  if (p.includes('republican')) return 'bg-party-republican';
  return 'bg-accent-300';
}

/**
 * Party colour for chart/SVG contexts, which take a CSS value rather than a class.
 *
 * Returns CSS variables (see index.css) that derive from the party tokens, so these
 * follow dark mode and cannot drift from party colours elsewhere in the app. The
 * previous hardcoded values had already drifted: #4361EE / #E63946 were returned
 * under comments claiming party.democrat.DEFAULT / party.republican.DEFAULT, which
 * are #4F46E5 / #DC2626.
 */
export function getPartyHex(party: string | null | undefined): string {
  if (!party) return 'var(--cf-party-none)';
  const p = party.toLowerCase();
  if (p.includes('democrat')) return 'var(--cf-party-democrat)';
  if (p.includes('republican')) return 'var(--cf-party-republican)';
  return 'var(--cf-party-none)';
}

export function buildQueryParams(query: CFQuery, extra?: { offset?: number; limit?: number; sort_by?: string; sort_order?: 'asc' | 'desc' }): URLSearchParams {
  const params = new URLSearchParams();
  if (extra?.offset !== undefined) params.set('offset', String(extra.offset));
  if (extra?.limit !== undefined) params.set('limit', String(extra.limit));
  if (extra?.sort_by) params.set('sort_by', extra.sort_by);
  if (extra?.sort_order) params.set('sort_order', extra.sort_order);

  if (query.searchQuery) params.set('search', query.searchQuery);
  if (query.contributor) params.set('contributor_name', query.contributor);
  if (query.committee_name) params.set('committee_name', query.committee_name);
  if (query.candidate) params.set('candidate_name', query.candidate);
  if (query.contribution_type) params.set('contribution_type', query.contribution_type);
  if (query.contributor_type) params.set('contributor_type', query.contributor_type);
  if (query.committee_type) params.set('committee_type', query.committee_type);
  if (query.start_date) params.set('start_date', query.start_date);
  if (query.end_date) params.set('end_date', query.end_date);
  if (query.min_amount) params.set('min_amount', query.min_amount);
  if (query.max_amount) params.set('max_amount', query.max_amount);
  if (query.year) params.set('year', String(query.year));
  if (query.employer) params.set('employer', query.employer);
  if (query.office) params.set('office', query.office);
  if (query.group_by) params.set('group_by', query.group_by);
  if (query.current_cycle) params.set('current_cycle', 'true');

  return params;
}

export function queryToUrlParams(query: CFQuery): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '' && k !== 'scope') {
      params.set(k, String(v));
    }
  }
  if (query.scope && query.scope !== 'any') params.set('scope', query.scope);
  return params.toString();
}

export function urlParamsToQuery(search: string): CFQuery {
  const params = new URLSearchParams(search);
  const query: CFQuery = {};
  if (params.get('searchQuery')) query.searchQuery = params.get('searchQuery')!;
  if (params.get('scope')) query.scope = params.get('scope') as CFQuery['scope'];
  if (params.get('contributor')) query.contributor = params.get('contributor')!;
  if (params.get('committee_name')) query.committee_name = params.get('committee_name')!;
  if (params.get('candidate')) query.candidate = params.get('candidate')!;
  if (params.get('office')) query.office = params.get('office')!;
  if (params.get('contributor_type')) query.contributor_type = params.get('contributor_type')!;
  if (params.get('committee_type')) query.committee_type = params.get('committee_type')!;
  if (params.get('contribution_type')) query.contribution_type = params.get('contribution_type')!;
  if (params.get('party')) query.party = params.get('party') as CFQuery['party'];
  if (params.get('office_chamber')) query.office_chamber = params.get('office_chamber') as CFQuery['office_chamber'];
  if (params.get('year')) query.year = Number(params.get('year'));
  if (params.get('start_date')) query.start_date = params.get('start_date')!;
  if (params.get('end_date')) query.end_date = params.get('end_date')!;
  if (params.get('min_amount')) query.min_amount = params.get('min_amount')!;
  if (params.get('max_amount')) query.max_amount = params.get('max_amount')!;
  if (params.get('employer')) query.employer = params.get('employer')!;
  if (params.get('current_cycle') === 'true') query.current_cycle = true;
  if (params.get('group_by') === 'contributor') query.group_by = 'contributor';
  if (params.get('mode') === 'expenditures') query.mode = 'expenditures';
  if (params.get('payee')) query.payee = params.get('payee')!;
  if (params.get('vendor_id')) query.vendor_id = params.get('vendor_id')!;
  if (params.get('expenditure_type')) query.expenditure_type = params.get('expenditure_type')!;
  if (params.get('payment_type')) query.payment_type = params.get('payment_type')!;
  if (params.get('disbursement_type')) query.disbursement_type = params.get('disbursement_type')!;
  return query;
}
